// src/features/settings/components/non-working-days-panel/NonWorkingDayDialog.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Alert,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Repeat as RepeatIcon,
  BeachAccess as HolidayIcon,
  EventBusy as ClosingDayIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { useNonWorkingDays } from '../../hooks/useNonWorkingDays';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import RRuleInput from '../../../../components/common/RRuleInput';

const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function NonWorkingDayDialog({ open, nonWorkingDay, onClose }) {
  const { t } = useTranslation(['settings', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const {
    createPublicHoliday,
    updatePublicHolidayHours,
    createClosingDayDateRange,
    createClosingDayRRule,
    updateClosingDayDateRange,
    updateClosingDayRRule,
    loading,
  } = useNonWorkingDays();

  const isEdit = Boolean(nonWorkingDay);
  // Editing a public holiday is a distinct, lightweight "hours only" mode.
  const isHolidayHoursEdit = isEdit && nonWorkingDay.kind === 'PUBLIC_HOLIDAY';

  // Top-level kind: only selectable on create
  const [kind, setKind] = useState('PUBLIC_HOLIDAY');
  // Closing-day sub-type: date_range or rrule
  const [closingType, setClosingType] = useState('date_range');

  const [name, setName] = useState('');
  const [date, setDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rrule, setRRule] = useState('');
  const [isFullDay, setIsFullDay] = useState(true);
  const [freeFrom, setFreeFrom] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (nonWorkingDay) {
      setName(nonWorkingDay.name || '');
      setIsFullDay(nonWorkingDay.is_full_day ?? true);
      setFreeFrom(nonWorkingDay.free_from ? nonWorkingDay.free_from.slice(0, 5) : '');

      if (nonWorkingDay.kind === 'PUBLIC_HOLIDAY') {
        setKind('PUBLIC_HOLIDAY');
        setDate(nonWorkingDay.start_date ? new Date(nonWorkingDay.start_date) : null);
      } else {
        setKind('CLOSING_DAY');
        if (nonWorkingDay.rrule) {
          setClosingType('rrule');
          setRRule(nonWorkingDay.rrule);
          setStartDate(null);
          setEndDate(null);
        } else {
          setClosingType('date_range');
          setStartDate(nonWorkingDay.start_date ? new Date(nonWorkingDay.start_date) : null);
          setEndDate(nonWorkingDay.end_date ? new Date(nonWorkingDay.end_date) : null);
          setRRule('');
        }
      }
    } else {
      // Reset for a new entry
      setKind('PUBLIC_HOLIDAY');
      setClosingType('date_range');
      setName('');
      setDate(null);
      setStartDate(null);
      setEndDate(null);
      setRRule('');
      setIsFullDay(true);
      setFreeFrom('');
    }
    setValidationError('');
  }, [nonWorkingDay, open]);

  const handleKindChange = (event, newKind) => {
    if (newKind !== null) {
      setKind(newKind);
      setValidationError('');
    }
  };

  const handleClosingTypeChange = (event, newType) => {
    if (newType !== null) {
      setClosingType(newType);
      setValidationError('');
    }
  };

  const validate = () => {
    if (isHolidayHoursEdit) {
      if (!isFullDay && !freeFrom) {
        setValidationError(t('settings:non_working_days.validation.free_from_required'));
        return false;
      }
      return true;
    }

    if (!name.trim()) {
      setValidationError(t('settings:non_working_days.validation.name_required'));
      return false;
    }

    if (kind === 'PUBLIC_HOLIDAY') {
      if (!date) {
        setValidationError(t('settings:non_working_days.validation.date_required'));
        return false;
      }
      if (!isFullDay && !freeFrom) {
        setValidationError(t('settings:non_working_days.validation.free_from_required'));
        return false;
      }
      return true;
    }

    // CLOSING_DAY
    if (closingType === 'date_range') {
      if (!startDate) {
        setValidationError(t('settings:non_working_days.validation.start_date_required'));
        return false;
      }
      if (!endDate) {
        setValidationError(t('settings:non_working_days.validation.end_date_required'));
        return false;
      }
      if (startDate > endDate) {
        setValidationError(t('settings:non_working_days.validation.invalid_date_range'));
        return false;
      }
    } else if (!rrule.trim()) {
      setValidationError(t('settings:non_working_days.validation.rrule_required'));
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    let result;

    if (isHolidayHoursEdit) {
      result = await updatePublicHolidayHours(nonWorkingDay.non_working_day_id, {
        is_full_day: isFullDay,
        free_from: isFullDay ? null : freeFrom || null,
      });
    } else if (kind === 'PUBLIC_HOLIDAY') {
      result = await createPublicHoliday({
        date: formatLocalDate(date),
        name: name.trim(),
        is_full_day: isFullDay,
        free_from: isFullDay ? null : freeFrom || null,
      });
    } else if (closingType === 'date_range') {
      const data = {
        name: name.trim(),
        start_date: formatLocalDate(startDate),
        end_date: formatLocalDate(endDate),
      };
      result = isEdit
        ? await updateClosingDayDateRange(nonWorkingDay.non_working_day_id, data)
        : await createClosingDayDateRange(data);
    } else {
      const data = {
        name: name.trim(),
        rrule: rrule.trim(),
      };
      result = isEdit
        ? await updateClosingDayRRule(nonWorkingDay.non_working_day_id, data)
        : await createClosingDayRRule(data);
    }

    if (result.success) {
      showSuccess(
        isEdit
          ? t('settings:non_working_days.success.updated')
          : t('settings:non_working_days.success.created')
      );
      onClose(true);
    } else {
      showError(result.error);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  const dialogTitle = isHolidayHoursEdit
    ? t('settings:non_working_days.dialog.edit_hours_title')
    : isEdit
      ? t('settings:non_working_days.dialog.edit_title')
      : t('settings:non_working_days.dialog.create_title');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth={closingType === 'rrule' && kind === 'CLOSING_DAY' ? 'md' : 'sm'} fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent>
        {/* Lightweight holiday "edit hours" mode */}
        {isHolidayHoursEdit && (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {nonWorkingDay.name} — {nonWorkingDay.start_date}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(isFullDay)}
                  onChange={(e) => setIsFullDay(e.target.checked)}
                />
              }
              label={t('settings:non_working_days.labels.is_full_day')}
              sx={{ mt: 1 }}
            />
            {!isFullDay && (
              <TextField
                fullWidth
                label={t('settings:non_working_days.labels.free_from')}
                type="time"
                value={freeFrom}
                onChange={(e) => setFreeFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                sx={{ maxWidth: 200 }}
              />
            )}
          </Box>
        )}

        {!isHolidayHoursEdit && (
          <>
            {/* Kind selection (only for create) */}
            {!isEdit && (
              <Box mb={3} mt={1}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('settings:non_working_days.dialog.kind_label')}
                </Typography>
                <ToggleButtonGroup
                  value={kind}
                  exclusive
                  onChange={handleKindChange}
                  aria-label="non-working day kind"
                  fullWidth
                >
                  <ToggleButton value="PUBLIC_HOLIDAY" aria-label="public holiday">
                    <HolidayIcon sx={{ mr: 1 }} />
                    {t('settings:non_working_days.types.public_holiday')}
                  </ToggleButton>
                  <ToggleButton value="CLOSING_DAY" aria-label="closing day">
                    <ClosingDayIcon sx={{ mr: 1 }} />
                    {t('settings:non_working_days.types.closing_day')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <TextField
              fullWidth
              label={t('settings:non_working_days.dialog.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
              required
              inputProps={{ maxLength: 255 }}
            />

            {/* Public holiday fields */}
            {kind === 'PUBLIC_HOLIDAY' && (
              <>
                <Box mt={1}>
                  <DatePicker
                    label={t('settings:non_working_days.labels.date')}
                    value={date}
                    onChange={(newValue) => setDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        margin: 'normal',
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(isFullDay)}
                        onChange={(e) => setIsFullDay(e.target.checked)}
                      />
                    }
                    label={t('settings:non_working_days.labels.is_full_day')}
                    sx={{ display: 'block', mt: 1 }}
                  />
                  {!isFullDay && (
                    <TextField
                      label={t('settings:non_working_days.labels.free_from')}
                      type="time"
                      value={freeFrom}
                      onChange={(e) => setFreeFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      sx={{ maxWidth: 200 }}
                    />
                  )}
                </Box>
              </>
            )}

            {/* Closing day fields */}
            {kind === 'CLOSING_DAY' && (
              <Box mt={2}>
                <ToggleButtonGroup
                  value={closingType}
                  exclusive
                  onChange={handleClosingTypeChange}
                  aria-label="closing day type"
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="date_range" aria-label="date range">
                    <CalendarIcon sx={{ mr: 1 }} />
                    {t('settings:non_working_days.types.date_range')}
                  </ToggleButton>
                  <ToggleButton value="rrule" aria-label="rrule">
                    <RepeatIcon sx={{ mr: 1 }} />
                    {t('settings:non_working_days.types.rrule')}
                  </ToggleButton>
                </ToggleButtonGroup>

                {closingType === 'date_range' && (
                  <>
                    <Box mt={2}>
                      <DatePicker
                        label={t('settings:non_working_days.labels.start_date')}
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            margin: 'normal',
                          },
                        }}
                      />
                      <DatePicker
                        label={t('settings:non_working_days.labels.end_date')}
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        minDate={startDate}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            margin: 'normal',
                          },
                        }}
                      />
                    </Box>
                  </>
                )}

                {closingType === 'rrule' && (
                  <Box mt={2}>
                    <RRuleInput rrule={rrule} setRrule={setRRule} />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {validationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {isEdit ? t('common:buttons.save') : t('common:buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
